"use client";

import { cva } from "class-variance-authority";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { Heading, Select } from "ui-components";

import { ProfileTabsEnum } from "@/src/@types/Profile";
import { useAuth } from "@/src/hooks/useAuth";
import {
  hasPermission,
  isUserAnOrganizationAdmin,
} from "@/src/utils/organization";

const tabLinkClasses = cva("whitespace-nowrap py-1 text-sm font-medium", {
  variants: {
    current: {
      true: "border-primary border-b-2",
      false: "text-body-primary hover:text-primary",
    },
  },
  defaultVariants: {
    current: false,
  },
});

interface IProfileTabsProps {
  tab: string;
}

interface ITab {
  id: number;
  name: string;
  href: string;
  current: boolean;
  title: string;
  hidden?: boolean;
}
export function ProfileTabs({ tab }: Readonly<IProfileTabsProps>): JSX.Element {
  const router = useRouter();
  const { authData } = useAuth();
  const [tabs, setTabs] = useState<ITab[]>([]);

  useEffect(() => {
    if (authData) {
      const isOrgAdmin = isUserAnOrganizationAdmin(authData.userTaxonomy);
      const { organizationRoles } = authData;

      // Dynamically update the tabs based on permissions and other conditions
      const updatedTabs = [
        {
          id: 1,
          name: "Home",
          href: `/profile/${ProfileTabsEnum.HOME}`,
          current: tab === ProfileTabsEnum.HOME,
          title: `Welcome ${authData ? `${authData.first_name}!` : ""}`,
          hidden: authData?.isLegacyOrganizationAdmin || isOrgAdmin,
        },
        {
          id: 2,
          name: "Organization Profile",
          href: `/profile/${ProfileTabsEnum.ORGANIZATION_PROFILE}`,
          current: tab === ProfileTabsEnum.ORGANIZATION_PROFILE,
          title: "Organization Profile",
          hidden:
            !isOrgAdmin &&
            !hasPermission(organizationRoles, "View Dashboard Statistics"),
        },
        {
          id: 3,
          name: "My Saved Jobs",
          href: `/profile/${ProfileTabsEnum.MY_SAVED_JOBS}`,
          current: tab === ProfileTabsEnum.MY_SAVED_JOBS,
          title: "My Saved Jobs",
          hidden:
            authData?.isLegacyOrganizationAdmin ||
            isOrgAdmin ||
            authData.type === "agent",
        },
        {
          id: 4,
          name: "My Connections",
          href: `/profile/${ProfileTabsEnum.MY_CONNECTIONS}`,
          current: tab === ProfileTabsEnum.MY_CONNECTIONS,
          title: "My Connections",
          hidden:
            authData?.isLegacyOrganizationAdmin ||
            isOrgAdmin ||
            authData.type === "agent",
        },
        {
          id: 5,
          name: "My Messages",
          href: `/profile/${ProfileTabsEnum.MY_MESSAGES}`,
          current: tab === ProfileTabsEnum.MY_MESSAGES,
          title: "My Messages",
          hidden:
            authData?.isLegacyOrganizationAdmin ||
            isOrgAdmin ||
            authData.type === "agent",
        },
        {
          id: 6,
          name: "Resources Center",
          href: `/profile/${ProfileTabsEnum.MyDocuments}`,
          current: tab === ProfileTabsEnum.MyDocuments,
          title: "Resources Center",
          hidden:
            !isOrgAdmin &&
            (!hasPermission(organizationRoles, "View Dashboard Statistics") ||
              !hasPermission(organizationRoles, "View CSM")),
        },
        {
          id: 7,
          name: "Settings",
          href: `/profile/${ProfileTabsEnum.SETTINGS}`,
          current: tab === ProfileTabsEnum.SETTINGS,
          title: "Settings",
          hidden: false,
        },
      ];

      // Set the updated tabs in state
      setTabs(updatedTabs);
    }
  }, [authData, tab]);

  const activeTab = tabs.find((tabItem) => tabItem.current);

  return (
    <>
      <Heading>{activeTab?.title}</Heading>
      <div className="w-full">
        <div className="sm:hidden">
          <label htmlFor="tabs" className="sr-only">
            Select a tab
          </label>
          <Select
            value={tabs.find((tabItem) => tabItem.current)?.id}
            size="full"
            options={tabs.map((tabItem) => ({
              id: tabItem.id,
              title: tabItem.name,
            }))}
            onSelection={(id) => {
              router.push(
                tabs.find((tabItem) => tabItem.id === id)?.href ?? "/"
              );
            }}
          />
        </div>
        <div className="hidden sm:block">
          <div className="flex justify-center">
            <nav className="-mb-px flex space-x-5" aria-label="Tabs">
              {tabs.map((tabItem) => {
                if (tabItem.hidden) {
                  return null;
                }
                return (
                  <Link
                    key={tabItem.name}
                    href={tabItem.href}
                    className={tabLinkClasses({ current: tabItem.current })}
                    aria-current={tabItem.current ? "page" : undefined}
                  >
                    {tabItem.name}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      </div>
    </>
  );
}
